import { alpha } from '@mui/material/styles';

const teal = '#00897b';
const tealMuted = '#b2dfdb';

/**
 * Estilos do formulário/visualização de pedido alinhados ao tema MUI (claro/escuro).
 * @param {import('@mui/material/styles').Theme} theme
 */
export function getOrderThemeLayoutSx(theme) {
  const isDark = theme.palette.mode === 'dark';
  const border = theme.palette.divider;
  const muted = theme.palette.text.secondary;
  const textPrimary = theme.palette.text.primary;
  const paper = theme.palette.background.paper;
  const pMain = theme.palette.primary.main;
  const pDark = theme.palette.primary.dark;
  const pLight = theme.palette.primary.light;
  const bannerEnd = isDark ? pLight : '#1976d2';
  const cardShadow = isDark
    ? '0 1px 3px rgba(0, 0, 0, 0.45)'
    : '0 1px 3px rgba(0, 0, 0, 0.08)';
  const cardShadowAccent = isDark
    ? '0 1px 2px rgba(0, 0, 0, 0.35), 0 1px 8px rgba(0, 0, 0, 0.3)'
    : '0 1px 2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06)';

  const orderMockCardSx = {
    bgcolor: paper,
    border: `1px solid ${border}`,
    borderRadius: '10px',
    boxShadow: cardShadow,
  };

  const transitionBg = isDark
    ? `linear-gradient(180deg, ${alpha(teal, 0.12)} 0%, ${paper} 55%)`
    : 'linear-gradient(180deg, #fafcfc 0%, #ffffff 55%)';

  const xsItemBg = isDark ? alpha(theme.palette.common.white, 0.04) : '#f3f6fa';

  const orderItemRowSx = {
    py: { xs: '12px', md: '12px' },
    px: { xs: '12px', md: 0 },
    borderBottom: { xs: 'none', md: '1px solid' },
    borderColor: border,
    border: { xs: `1px solid ${border}`, md: 'none' },
    borderRadius: { xs: '8px', md: 0 },
    bgcolor: { xs: xsItemBg, md: 'transparent' },
    mb: { xs: '12px', md: 0 },
    '&:last-of-type': {
      borderBottom: { md: 'none' },
      mb: { xs: 0, md: 0 },
    },
  };

  return {
    ORDER_MOCK_BORDER: border,
    ORDER_MOCK_MUTED: muted,

    orderPageWrapSx: {
      maxWidth: 1280,
      mx: 'auto',
      width: '100%',
      px: { xs: '14px', sm: '20px' },
      pb: { xs: '24px', md: '48px' },
    },

    orderBannerSx: {
      background: `linear-gradient(135deg, ${pDark} 0%, ${pMain} 55%, ${bannerEnd} 100%)`,
      color: theme.palette.primary.contrastText,
      px: { xs: '12px', md: '20px' },
      py: '12px',
      borderRadius: '10px',
      mb: { xs: '12px', md: '20px' },
      fontSize: { xs: '12px', md: '13px' },
      lineHeight: 1.35,
      opacity: 0.95,
    },

    orderLayoutGridSx: {
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(260px, 300px)' },
      gridTemplateAreas: {
        xs: '"ord-main" "ord-obs" "ord-sum"',
        md: '"ord-main ord-side"',
      },
      gap: { xs: '12px', md: '24px' },
      alignItems: 'start',
      width: '100%',
    },

    orderMainColumnSx: {
      gridArea: 'ord-main',
      minWidth: 0,
    },

    orderSideRailSx: {
      gridArea: { md: 'ord-side' },
      display: { xs: 'contents', md: 'flex' },
      flexDirection: { md: 'column-reverse' },
      gap: { md: '16px' },
      position: { md: 'sticky' },
      top: { md: '88px' },
      alignSelf: { md: 'start' },
      width: { md: '100%' },
      maxWidth: { md: 300 },
      minWidth: 0,
      maxHeight: { md: 'calc(100vh - 88px - 24px)' },
      overflowY: { md: 'auto' },
      overscrollBehavior: { md: 'contain' },
    },

    orderRailObsGridSx: {
      gridArea: { xs: 'ord-obs', md: 'auto' },
      width: '100%',
      maxWidth: { md: 300 },
    },

    orderRailSummaryGridSx: {
      gridArea: { xs: 'ord-sum', md: 'auto' },
      width: '100%',
      maxWidth: { md: 300 },
    },

    orderMockCardSx,

    orderMockCardPadSx: {
      p: { xs: '14px 12px', md: '18px 20px' },
    },

    orderStackBelowSx: {
      mb: { xs: '12px', md: '16px' },
    },

    orderPageTitleSx: {
      m: 0,
      mb: { xs: '12px', md: '16px' },
      fontSize: { xs: '1.2rem', md: '1.5rem' },
      fontWeight: 400,
      lineHeight: 1.25,
      color: textPrimary,
    },

    orderFormRowSx: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: { xs: '10px', md: '16px' },
      alignItems: { xs: 'stretch', md: 'flex-end' },
      flexDirection: { xs: 'column', md: 'row' },
    },

    orderHintSx: {
      fontSize: '11px',
      color: muted,
      mt: '10px',
      m: 0,
      mb: 0,
    },

    orderSectionTitleSx: {
      m: 0,
      mb: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      color: textPrimary,
    },

    orderRailHeadingSx: {
      m: 0,
      mb: '12px',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: muted,
      fontWeight: 600,
    },

    orderItemsToolbarSx: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      mb: '12px',
    },

    orderItemRowSx,

    orderItemGridSx: {
      display: { xs: 'grid', md: 'flex' },
      gridTemplateColumns: { xs: '1fr 1fr', md: 'none' },
      flexWrap: { md: 'wrap' },
      gap: { xs: '10px', md: '10px' },
      alignItems: { md: 'flex-start' },
      '& > .order-item-desc': { gridColumn: { xs: '1 / -1' } },
      '& > .order-item-qty': { gridColumn: { xs: '1' } },
      '& > .order-item-price': { gridColumn: { xs: '2' } },
      '& > .order-item-sku': { gridColumn: { xs: '1 / -1' } },
      '& > .order-item-del': {
        gridColumn: { xs: '1 / -1' },
        justifySelf: { xs: 'start' },
        mt: { xs: '6px', md: 0 },
      },
    },

    orderItemSkuFieldSx: {
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '5px', md: '6px' },
      minWidth: { md: 200 },
      flex: { md: '0 1 280px' },
      width: { xs: '100%', md: 'auto' },
    },

    orderItemQtyFieldSx: {
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '5px', md: '6px' },
      width: { md: 100 },
      minWidth: 0,
    },

    orderItemPriceFieldSx: {
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '5px', md: '6px' },
      width: { md: 160 },
      minWidth: 0,
    },

    orderItemDescFieldSx: {
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '5px', md: '6px' },
      minWidth: { md: 200 },
      flex: { md: '1 1 200px' },
      width: { xs: '100%', md: 'auto' },
    },

    orderTimelineSectionSx: {
      ...orderMockCardSx,
      ...{
        p: { xs: '14px 12px', md: '18px 20px' },
      },
    },

    orderTimelineHeaderSx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px',
      mb: '14px',
    },

    orderTimelineTitleSx: {
      m: 0,
      fontSize: '1.05rem',
      fontWeight: 600,
      color: textPrimary,
    },

    orderTimelineIngestOuterSx: {
      mb: { xs: '12px', md: '20px' },
    },

    orderTimelineHistoryTitleSx: {
      m: 0,
      mt: '24px',
      mb: 0,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: textPrimary,
    },

    orderSummaryLinesSx: {
      display: 'grid',
      gap: '10px',
      fontSize: '13px',
    },

    orderSummaryTotalSx: {
      mt: '12px',
      pt: '12px',
      borderTop: `1px solid ${border}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      fontWeight: 700,
      fontSize: '1.05rem',
    },

    orderRailHintSx: {
      fontSize: '11px',
      color: muted,
      mt: '10px',
      m: 0,
      mb: 0,
    },

    orderStatusTransitionCardSx: {
      ...orderMockCardSx,
      ...{
        p: { xs: '14px 12px', md: '18px 20px' },
        mb: { xs: '12px', md: '16px' },
      },
      background: transitionBg,
      borderLeft: `3px solid ${teal}`,
      boxShadow: cardShadowAccent,
    },

    orderStatusTransitionHeaderRowSx: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 1.25,
      mb: 1.25,
    },

    orderStatusTransitionTitleSx: {
      m: 0,
      fontSize: '1rem',
      fontWeight: 700,
      color: textPrimary,
      letterSpacing: '-0.01em',
      flex: '1 1 auto',
    },

    orderStatusTransitionDraftChipSx: {
      height: 22,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: isDark ? alpha(tealMuted, 0.95) : '#00695c',
      bgcolor: isDark ? alpha(teal, 0.28) : '#e0f2f1',
      border: 'none',
      '& .MuiChip-label': { px: 1 },
    },

    orderStatusTransitionHintSx: {
      fontSize: '12px',
      lineHeight: 1.5,
      color: muted,
      m: 0,
      mb: 2,
      maxWidth: 720,
    },

    orderStatusTransitionDropZoneSx: {
      display: 'block',
      width: '100%',
      mt: 1.5,
      py: 2.25,
      px: 2,
      borderRadius: '10px',
      border: `2px dashed ${isDark ? alpha(tealMuted, 0.45) : tealMuted}`,
      bgcolor: isDark ? alpha(teal, 0.12) : 'rgba(224, 242, 241, 0.65)',
      cursor: 'pointer',
      textAlign: 'center',
      font: 'inherit',
      fontFamily: 'inherit',
      color: 'inherit',
      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        bgcolor: isDark ? alpha(teal, 0.2) : 'rgba(216, 240, 238, 0.95)',
        borderColor: '#26a69a',
        boxShadow: isDark ? '0 0 0 1px rgba(255,255,255,0.06)' : '0 0 0 1px rgba(0, 137, 123, 0.12)',
      },
      '&:focus-visible': {
        outline: `2px solid ${teal}`,
        outlineOffset: 2,
      },
    },

    orderStatusTransitionDropTitleSx: {
      fontSize: '13px',
      fontWeight: 600,
      color: isDark ? tealMuted : teal,
      m: 0,
    },

    orderStatusTransitionDropSubSx: {
      fontSize: '11px',
      color: muted,
      mt: 0.75,
      m: 0,
      lineHeight: 1.45,
    },

    orderStatusTransitionChipsRowSx: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 0.75,
      mt: 1.5,
    },

    orderStatusTransitionFileChipSx: {
      height: 26,
      fontSize: '12px',
      fontWeight: 500,
      bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#eceff1',
      color: isDark ? textPrimary : '#37474f',
      border: `1px solid ${border}`,
    },
  };
}
